import React from "react";
import { useMemo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import worldData from "world-atlas/countries-110m.json";

// Map of our app country name -> topojson name(s)
const APP_TO_TOPO: Record<string, string> = {
  "United States": "United States of America",
  "Türkiye": "Turkey",
  "Czechia": "Czech Republic",
  "South Korea": "South Korea",
};

function appNameToTopo(name: string) {
  return APP_TO_TOPO[name] ?? name;
}

interface Props {
  countryCounts: Record<string, number>;
  selected?: string;
  onCountryClick: (country: string | undefined) => void;
  onHover?: (country: string | undefined) => void;
}

export const WorldMap: React.FC<Props> = ({ countryCounts, selected, onCountryClick, onHover }) => {
  const max = useMemo(
    () => Math.max(1, ...Object.values(countryCounts)),
    [countryCounts],
  );

  const topoToApp = useMemo(() => {
    const m: Record<string, string> = {};
    for (const app of Object.keys(countryCounts)) {
      m[appNameToTopo(app)] = app;
    }
    return m;
  }, [countryCounts]);

  const colorFor = (count: number) => {
    if (!count) return "var(--bg-surface)";
    const t = Math.min(1, count / max);
    const alpha = 0.25 + t * 0.75;
    return `color-mix(in oklab, var(--action-color) ${Math.round(alpha * 100)}%, var(--bg-surface))`;
  };

  return (
    <div className="ih-world-map">
      <ComposableMap
        projectionConfig={{ scale: 155 }}
        projection="geoMercator"
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup center={[10, 25]} zoom={1} minZoom={1} maxZoom={4}>
          <Geographies geography={worldData as unknown as Record<string, unknown>}>
            {({ geographies }: { geographies: Array<{ rsmKey: string; properties: { name: string } }> }) =>
              geographies.map((geo) => {
                const topoName = geo.properties.name;
                const appName = topoToApp[topoName];
                const count = appName ? countryCounts[appName] ?? 0 : 0;
                const isSelected = selected && appName === selected;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => onHover?.(appName)}
                    onMouseLeave={() => onHover?.(undefined)}
                    onClick={() => {
                      if (!appName) return;
                      onCountryClick(appName === selected ? undefined : appName);
                    }}
                    style={{
                      default: {
                        fill: colorFor(count),
                        stroke: "var(--edge-divider)",
                        strokeWidth: isSelected ? 1.5 : 0.4,
                        outline: "none",
                        cursor: appName ? "pointer" : "default",
                      },
                      hover: {
                        fill: appName
                          ? "color-mix(in oklab, var(--action-color) 85%, var(--background))"
                          : colorFor(count),
                        stroke: "var(--foreground)",
                        strokeWidth: 0.8,
                        outline: "none",
                        cursor: appName ? "pointer" : "default",
                      },
                      pressed: { fill: "var(--action-color)", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
}
